export const LOCATION_PATH_SEPARATOR = " > ";

export type LocationDisplayMode =
  | { kind: "tier"; tierIndex: number }
  | { kind: "fullPath" };

export type LocationTierLabelResolver = (tierIndex: number) => string;

export interface LocationTooltipRow {
  tierIndex: number;
  label: string;
  value: string;
  isDeepest: boolean;
}

export interface LocationCellDisplay {
  primary: string | null;
  context: string | null;
  breadcrumb: { value: string; isDeepest: boolean }[] | null;
}

const EMPTY_PLACEHOLDER = "—";

export function parseLocationPath(
  fullPath: string | null | undefined,
): string[] {
  if (!fullPath?.trim()) {
    return [];
  }
  return fullPath
    .split(LOCATION_PATH_SEPARATOR)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

export function deriveMaxLocationTierCount(paths: string[][]): number {
  if (!paths.length) {
    return 1;
  }
  const max = paths.reduce((acc, tiers) => Math.max(acc, tiers.length), 0);
  return max <= 0 ? 1 : max;
}

export function getDefaultLocationDisplayMode(
  tierCount: number,
): LocationDisplayMode {
  const safeCount = tierCount <= 0 ? 1 : tierCount;
  return { kind: "tier", tierIndex: safeCount - 1 };
}

export function locationTierLabelKey(tierIndex: number): string {
  return `LocationTierLabel_Lvl_${tierIndex}`;
}

export function getFullPathModeLabel(
  t: (key: string) => string,
): string {
  const key = "LocationDisplayMode_FullPath";
  const translated = t(key);
  return translated === key ? "Full path" : translated;
}

export function getLocationModeLabel(
  mode: LocationDisplayMode,
  tierCount: number,
  t: (key: string) => string,
): string {
  if (mode.kind === "fullPath") {
    return getFullPathModeLabel(t);
  }
  return t(locationTierLabelKey(mode.tierIndex));
}

export function buildLocationDisplayModeOptions(
  tierCount: number,
  t: (key: string) => string,
): LocationDisplayMode[] {
  const safeCount = tierCount <= 0 ? 1 : tierCount;
  const options: LocationDisplayMode[] = [];
  for (let i = safeCount - 1; i >= 0; i -= 1) {
    options.push({ kind: "tier", tierIndex: i });
  }
  options.push({ kind: "fullPath" });
  return options;
}

export function getLocationCellDisplay(
  tiers: string[],
  mode: LocationDisplayMode,
): LocationCellDisplay {
  if (!tiers.length) {
    return { primary: null, context: null, breadcrumb: null };
  }

  if (mode.kind === "fullPath") {
    const populated = tiers
      .map((value, tierIndex) => ({ value, tierIndex }))
      .filter((entry) => entry.value?.trim());
    if (!populated.length) {
      return { primary: null, context: null, breadcrumb: null };
    }
    const deepestIndex = populated[populated.length - 1].tierIndex;
    return {
      primary: null,
      context: null,
      breadcrumb: populated.map((entry) => ({
        value: entry.value,
        isDeepest: entry.tierIndex === deepestIndex,
      })),
    };
  }

  const primary = tiers[mode.tierIndex]?.trim() || null;
  const context =
    mode.tierIndex > 0
      ? tiers[mode.tierIndex - 1]?.trim() || null
      : null;

  return { primary, context, breadcrumb: null };
}

export function getLocationTooltipRows(
  tiers: string[],
  t: (key: string) => string,
): LocationTooltipRow[] {
  const populated = tiers
    .map((value, tierIndex) => ({ value: value?.trim(), tierIndex }))
    .filter((entry) => entry.value);

  if (!populated.length) {
    return [];
  }

  const deepestIndex = populated[populated.length - 1].tierIndex;
  return populated.map((entry) => ({
    tierIndex: entry.tierIndex,
    label: t(locationTierLabelKey(entry.tierIndex)),
    value: entry.value as string,
    isDeepest: entry.tierIndex === deepestIndex,
  }));
}

export function formatLocationCellPrimary(
  display: LocationCellDisplay,
): string {
  if (display.breadcrumb) {
    return display.breadcrumb.map((s) => s.value).join(LOCATION_PATH_SEPARATOR);
  }
  return display.primary ?? EMPTY_PLACEHOLDER;
}
